import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useCallback, type MouseEventHandler } from "react";

import {
  classNames,
  FONT_WEIGHT_NORMAL,
  FONT_WEIGHT_SEMI_BOLD,
  type MediaIconType,
} from "@skilitics/designer-ui-components";

import { MediaIcon } from "./MediaIcon";

const duration = 150;

const transition = css`
  transition:
    color ${duration}ms ease-in-out,
    background-color ${duration}ms ease-in-out,
    border ${duration}ms ease-in-out;
`;

const IconContainer = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 60px;
  height: 60px;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  width: fit-content;
  height: 100%;
`;

const Question = styled.span`
  color: ${({ theme }) => theme.generalTextIcon};

  font-size: ${({ theme }) => theme.fontSizeLarge};
  font-weight: ${FONT_WEIGHT_NORMAL};
`;

const Action = styled.span`
  color: ${({ theme }) => theme.primaryStrong};

  font-size: ${({ theme }) => theme.fontSizeLarge};
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;

  flex: 1 1 48%;
  min-width: 276px;
  height: 80px;

  background-color: ${({ theme }) => theme.surface};

  padding: 10px;

  border-radius: 5px;
  border: ${({ theme }) => `1px solid ${theme.outlineSoft}`};

  cursor: pointer;

  user-select: none;

  &:hover {
    border: ${({ theme }) => `1px solid ${theme.outlineDark}`};
  }

  &.selected {
    color: ${({ theme }) => theme.primaryStrong};
    border: ${({ theme }) => `1px solid ${theme.primaryStrong}`};
    background-color: ${({ theme }) => theme.primaryContainer};
  }

  &.selected ${MediaIcon} {
    filter: grayscale(0%);
  }

  &.hidden {
    display: none;
  }

  ${transition}
`;

type Props = {
  action: string;
  question: string;
  mediaIconType: MediaIconType;
  selected: boolean;
  hidden: boolean;
  onSelect: () => void;
  onConfirm: () => void;
};

export const CreateCard = ({
  action,
  question,
  mediaIconType,
  selected,
  hidden,
  onSelect,
  onConfirm,
  ...props
}: Props) => {
  const handleSelect: MouseEventHandler = useCallback(
    (event) => {
      event.stopPropagation();
      onSelect();
    },
    [onSelect],
  );

  const handleConfirm: MouseEventHandler = useCallback(
    (event) => {
      event.stopPropagation();
      onConfirm();
    },
    [onConfirm],
  );

  return (
    <Container
      className={classNames({ selected, hidden })}
      onClick={handleSelect}
      onDoubleClick={handleConfirm}
      {...props}
    >
      <IconContainer>
        <MediaIcon mediaIconType={mediaIconType} />
      </IconContainer>
      <Details>
        <Question>{question}</Question>
        <Action>{action}</Action>
      </Details>
    </Container>
  );
};
